import React, { useState, useEffect, useCallback } from 'react';
import axios from '../config/api';
import { API_URL } from '../config/api';
import {
  MdRefresh, MdEdit, MdSave, MdCancel, MdWarning, MdCheckCircle,
  MdSpeed, MdDataUsage, MdError, MdTimeline, MdSettings, MdInfo
} from 'react-icons/md';

function AdminApiUsage() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingConfig, setEditingConfig] = useState(false);
  const [editedConfig, setEditedConfig] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('day');
  const [periodStats, setPeriodStats] = useState(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/api-usage/summary`);
      setSummary(res.data);
    } catch (err) {
      console.error('Failed to fetch API usage summary:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPeriodStats = useCallback(async (period) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/api-usage/stats?period=${period}`);
      setPeriodStats(res.data.stats);
    } catch (err) {
      console.error('Failed to fetch period stats:', err);
    }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  useEffect(() => { fetchPeriodStats(selectedPeriod); }, [selectedPeriod, fetchPeriodStats]);

  const startEdit = () => {
    setEditedConfig({ ...summary.config });
    setEditingConfig(true);
    setMessage('');
  };

  const cancelEdit = () => {
    setEditingConfig(false);
    setMessage('');
  };

  const saveConfig = async () => {
    setSaving(true);
    setMessage('');
    try {
      await axios.put(`${API_URL}/api/admin/api-usage/config`, editedConfig);
      setEditingConfig(false);
      setMessage('Configuration updated successfully');
      fetchSummary();
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Failed to update configuration');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setEditedConfig(prev => ({ ...prev, [field]: parseInt(value) || 0 }));
  };

  const updateTextField = (field, value) => {
    setEditedConfig(prev => ({ ...prev, [field]: value }));
  };

  // Determine bar color based on percentage
  const getBarColor = (pct, warningPct) => {
    if (pct >= 100) return 'bg-red-500';
    if (pct >= warningPct) return 'bg-amber-500';
    if (pct >= 50) return 'bg-sky-500';
    return 'bg-emerald-500';
  };

  const getStatusDot = (pct, warningPct) => {
    if (pct >= 100) return 'text-red-500';
    if (pct >= warningPct) return 'text-amber-500';
    return 'text-emerald-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" data-testid="api-usage-loading"></div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="max-w-6xl mx-auto px-4 pt-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <MdError className="text-red-500 text-3xl mx-auto mb-2" />
          <p className="text-red-700 font-rubik" data-testid="api-usage-error">Failed to load API usage data. Please try again.</p>
          <button onClick={fetchSummary} className="mt-3 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { config, rate_check, daily_breakdown, feature_breakdown } = summary;

  return (
    <div className="max-w-6xl mx-auto px-4 pt-8 pb-6" data-testid="admin-api-usage-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md">
            <MdDataUsage className="text-white text-2xl" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800 font-rubik" data-testid="api-usage-title">Gemini API Usage</h1>
            <p className="text-sm text-gray-500 font-rubik">Monitor usage, rate limits & tier limitations</p>
          </div>
        </div>
        <button
          onClick={() => { fetchSummary(); fetchPeriodStats(selectedPeriod); }}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors font-rubik text-sm"
          data-testid="refresh-api-usage-btn"
        >
          <MdRefresh className="text-lg" /> Refresh
        </button>
      </div>

      {message && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm font-rubik ${message.includes('success') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-testid="api-usage-message">
          {message}
        </div>
      )}

      {/* Any Warnings Banner */}
      {rate_check.any_warning && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3" data-testid="rate-limit-warning-banner">
          <MdWarning className="text-amber-500 text-2xl flex-shrink-0" />
          <div>
            <p className="font-medium text-amber-800 font-rubik">Rate Limit Warning</p>
            <p className="text-sm text-amber-700 font-rubik">One or more usage metrics have exceeded the configured warning threshold. Review the rate limits below.</p>
          </div>
        </div>
      )}

      {/* Tier Info Card */}
      <div className="bg-white rounded-xl border shadow-card p-5 mb-6" data-testid="tier-info-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <MdInfo className="text-indigo-500 text-xl" />
            <h2 className="text-lg font-semibold text-gray-800 font-rubik">Current Tier</h2>
          </div>
          <span className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-sm font-medium font-rubik" data-testid="tier-name-badge">
            {config.tier_name}
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-500 font-rubik mb-1">Model</p>
            <p className="text-sm font-semibold text-gray-800 font-rubik" data-testid="tier-model">{config.model}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-500 font-rubik mb-1">Requests/Min</p>
            <p className="text-lg font-bold text-gray-800 font-rubik" data-testid="tier-rpm-limit">{config.rpm_limit}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-500 font-rubik mb-1">Requests/Day</p>
            <p className="text-lg font-bold text-gray-800 font-rubik" data-testid="tier-rpd-limit">{config.rpd_limit?.toLocaleString()}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-500 font-rubik mb-1">Tokens/Min</p>
            <p className="text-lg font-bold text-gray-800 font-rubik" data-testid="tier-tpm-limit">{config.tpm_limit?.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Rate Limit Gauges */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { key: 'rpm', label: 'Requests / Min', icon: <MdSpeed className="text-lg" /> },
          { key: 'rpd', label: 'Requests / Day', icon: <MdTimeline className="text-lg" /> },
          { key: 'tpm', label: 'Tokens / Min', icon: <MdDataUsage className="text-lg" /> },
          { key: 'monthly', label: 'Monthly Requests', icon: <MdTimeline className="text-lg" /> },
        ].map(({ key, label, icon }) => {
          const d = rate_check[key];
          return (
            <div key={key} className="bg-white rounded-xl border shadow-card p-4" data-testid={`rate-gauge-${key}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={getStatusDot(d.usage_pct, d.warning_pct)}>{icon}</span>
                  <span className="text-sm font-medium text-gray-700 font-rubik">{label}</span>
                </div>
                {d.warning && <MdWarning className="text-amber-500" data-testid={`rate-warning-${key}`} />}
              </div>
              <div className="flex items-end justify-between mb-2">
                <span className="text-2xl font-bold text-gray-800 font-rubik" data-testid={`rate-current-${key}`}>
                  {d.current.toLocaleString()}
                </span>
                <span className="text-sm text-gray-400 font-rubik">/ {d.limit.toLocaleString()}</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full transition-all duration-500 ${getBarColor(d.usage_pct, d.warning_pct)}`}
                  style={{ width: `${Math.min(d.usage_pct, 100)}%` }}
                  data-testid={`rate-bar-${key}`}
                ></div>
              </div>
              <p className="text-xs text-gray-400 font-rubik mt-1">{d.usage_pct}% used (warn at {d.warning_pct}%)</p>
            </div>
          );
        })}
      </div>

      {/* Stats Period Selector & Stats */}
      <div className="bg-white rounded-xl border shadow-card p-5 mb-6" data-testid="usage-stats-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800 font-rubik flex items-center gap-2">
            <MdTimeline className="text-indigo-500" /> Usage Statistics
          </h2>
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            {['day', 'week', 'month'].map(p => (
              <button
                key={p}
                onClick={() => setSelectedPeriod(p)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all font-rubik ${
                  selectedPeriod === p ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid={`period-btn-${p}`}
              >
                {p === 'day' ? 'Today' : p === 'week' ? 'Week' : 'Month'}
              </button>
            ))}
          </div>
        </div>

        {periodStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
            <div className="bg-emerald-50 rounded-lg p-3">
              <p className="text-xs text-emerald-600 font-rubik mb-1">Total Calls</p>
              <p className="text-xl font-bold text-emerald-800 font-rubik" data-testid="stats-total-calls">{periodStats.total_calls}</p>
            </div>
            <div className="bg-sky-50 rounded-lg p-3">
              <p className="text-xs text-sky-600 font-rubik mb-1">Tokens Used</p>
              <p className="text-xl font-bold text-sky-800 font-rubik" data-testid="stats-total-tokens">{periodStats.total_tokens.toLocaleString()}</p>
            </div>
            <div className="bg-red-50 rounded-lg p-3">
              <p className="text-xs text-red-600 font-rubik mb-1">Failed Calls</p>
              <p className="text-xl font-bold text-red-800 font-rubik" data-testid="stats-failed-calls">{periodStats.failed_calls}</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-3">
              <p className="text-xs text-purple-600 font-rubik mb-1">Avg Latency</p>
              <p className="text-xl font-bold text-purple-800 font-rubik" data-testid="stats-avg-latency">{periodStats.avg_latency_ms}ms</p>
            </div>
          </div>
        )}

        {periodStats && periodStats.total_calls > 0 && (
          <div>
            <p className="text-sm font-medium text-gray-600 font-rubik mb-2">Error Rate</p>
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-gray-100 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all ${periodStats.error_rate > 10 ? 'bg-red-500' : periodStats.error_rate > 0 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                  style={{ width: `${Math.max(periodStats.error_rate, 1)}%` }}
                ></div>
              </div>
              <span className="text-sm font-semibold text-gray-700 font-rubik" data-testid="stats-error-rate">{periodStats.error_rate}%</span>
            </div>
          </div>
        )}
      </div>

      {/* 7-Day Chart */}
      <div className="bg-white rounded-xl border shadow-card p-5 mb-6" data-testid="daily-breakdown-card">
        <h2 className="text-lg font-semibold text-gray-800 font-rubik mb-4 flex items-center gap-2">
          <MdTimeline className="text-indigo-500" /> 7-Day Breakdown
        </h2>
        <div className="overflow-x-auto">
          <div className="flex items-end gap-2 h-40 min-w-[400px]">
            {daily_breakdown.map((d, i) => {
              const maxCalls = Math.max(...daily_breakdown.map(x => x.calls), 1);
              const barHeight = (d.calls / maxCalls) * 100;
              const dayLabel = new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' });
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1" data-testid={`daily-bar-${d.date}`}>
                  <span className="text-xs text-gray-500 font-rubik">{d.calls}</span>
                  <div className="w-full flex justify-center">
                    <div
                      className={`w-8 rounded-t-md transition-all duration-300 ${d.errors > 0 ? 'bg-gradient-to-t from-red-400 to-indigo-400' : 'bg-gradient-to-t from-indigo-400 to-indigo-300'}`}
                      style={{ height: `${Math.max(barHeight, 4)}%`, minHeight: '4px' }}
                    ></div>
                  </div>
                  <span className="text-xs text-gray-400 font-rubik">{dayLabel}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex items-center gap-4 mt-3 text-xs text-gray-400 font-rubik">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-indigo-400 inline-block"></span> API Calls</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-400 inline-block"></span> Includes Errors</span>
        </div>
      </div>

      {/* Feature Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {Object.entries(feature_breakdown).map(([feat, data]) => (
          <div key={feat} className="bg-white rounded-xl border shadow-card p-4" data-testid={`feature-card-${feat}`}>
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${feat === 'poll_validation' ? 'bg-teal-100' : 'bg-orange-100'}`}>
                {feat === 'poll_validation' ? (
                  <MdCheckCircle className="text-teal-600" />
                ) : (
                  <MdDataUsage className="text-orange-600" />
                )}
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800 font-rubik">
                  {feat === 'poll_validation' ? 'Poll Validation' : feat === 'auto_tagging' ? 'Auto Tagging' : feat}
                </p>
                <p className="text-xs text-gray-400 font-rubik">Today's usage</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center">
                <p className="text-lg font-bold text-gray-800 font-rubik">{data.calls}</p>
                <p className="text-xs text-gray-400 font-rubik">Calls</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-gray-800 font-rubik">{data.tokens.toLocaleString()}</p>
                <p className="text-xs text-gray-400 font-rubik">Tokens</p>
              </div>
              <div className="text-center">
                <p className={`text-lg font-bold font-rubik ${data.errors > 0 ? 'text-red-600' : 'text-gray-800'}`}>{data.errors}</p>
                <p className="text-xs text-gray-400 font-rubik">Errors</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Errors */}
      {periodStats && periodStats.recent_errors.length > 0 && (
        <div className="bg-white rounded-xl border shadow-card p-5 mb-6" data-testid="recent-errors-card">
          <h2 className="text-lg font-semibold text-gray-800 font-rubik mb-3 flex items-center gap-2">
            <MdError className="text-red-500" /> Recent Errors
          </h2>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {periodStats.recent_errors.map((err, i) => (
              <div key={i} className="flex items-start gap-3 bg-red-50 rounded-lg p-3 text-sm" data-testid={`error-entry-${i}`}>
                <MdError className="text-red-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded font-rubik">{err.feature}</span>
                    <span className="text-xs text-gray-400 font-rubik">
                      {new Date(err.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-red-700 font-rubik text-xs truncate">{err.error}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Configuration Editor */}
      <div className="bg-white rounded-xl border shadow-card p-5" data-testid="config-editor-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800 font-rubik flex items-center gap-2">
            <MdSettings className="text-gray-500" /> Configuration
          </h2>
          {!editingConfig ? (
            <button onClick={startEdit} className="flex items-center gap-1 px-3 py-1.5 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-lg transition-colors text-sm font-rubik" data-testid="edit-config-btn">
              <MdEdit className="text-sm" /> Edit
            </button>
          ) : (
            <div className="flex gap-2">
              <button onClick={cancelEdit} className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors text-sm font-rubik" data-testid="cancel-config-btn">
                <MdCancel className="text-sm" /> Cancel
              </button>
              <button onClick={saveConfig} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white hover:opacity-90 rounded-lg transition-colors text-sm font-rubik disabled:opacity-50" data-testid="save-config-btn">
                <MdSave className="text-sm" /> {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Tier Settings */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-600 font-rubik border-b pb-1">Tier & Model</h3>
            <div>
              <label className="text-xs text-gray-500 font-rubik">Tier Name</label>
              {editingConfig ? (
                <input
                  type="text"
                  value={editedConfig.tier_name || ''}
                  onChange={e => updateTextField('tier_name', e.target.value)}
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm font-rubik focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                  data-testid="config-tier-name-input"
                />
              ) : (
                <p className="text-sm text-gray-800 font-rubik mt-1">{config.tier_name}</p>
              )}
            </div>
            <div>
              <label className="text-xs text-gray-500 font-rubik">Model</label>
              {editingConfig ? (
                <input
                  type="text"
                  value={editedConfig.model || ''}
                  onChange={e => updateTextField('model', e.target.value)}
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm font-rubik focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                  data-testid="config-model-input"
                />
              ) : (
                <p className="text-sm text-gray-800 font-rubik mt-1">{config.model}</p>
              )}
            </div>
          </div>

          {/* Rate Limits */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-600 font-rubik border-b pb-1">Rate Limits</h3>
            {[
              { key: 'rpm_limit', label: 'Requests per Minute (RPM)' },
              { key: 'rpd_limit', label: 'Requests per Day (RPD)' },
              { key: 'tpm_limit', label: 'Tokens per Minute (TPM)' },
              { key: 'monthly_quota', label: 'Monthly Request Quota' },
            ].map(({ key, label }) => (
              <div key={key}>
                <label className="text-xs text-gray-500 font-rubik">{label}</label>
                {editingConfig ? (
                  <input
                    type="number"
                    value={editedConfig[key] || 0}
                    onChange={e => updateField(key, e.target.value)}
                    className="mt-1 w-full px-3 py-2 border rounded-lg text-sm font-rubik focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                    data-testid={`config-${key}-input`}
                  />
                ) : (
                  <p className="text-sm text-gray-800 font-rubik mt-1">{config[key]?.toLocaleString()}</p>
                )}
              </div>
            ))}
          </div>

          {/* Warning Thresholds */}
          <div className="space-y-3 md:col-span-2">
            <h3 className="text-sm font-semibold text-gray-600 font-rubik border-b pb-1">Warning Thresholds (%)</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { key: 'rpm_warning_pct', label: 'RPM Warning %' },
                { key: 'rpd_warning_pct', label: 'RPD Warning %' },
                { key: 'tpm_warning_pct', label: 'TPM Warning %' },
                { key: 'monthly_warning_pct', label: 'Monthly Warning %' },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="text-xs text-gray-500 font-rubik">{label}</label>
                  {editingConfig ? (
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={editedConfig[key] || 80}
                      onChange={e => updateField(key, e.target.value)}
                      className="mt-1 w-full px-3 py-2 border rounded-lg text-sm font-rubik focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                      data-testid={`config-${key}-input`}
                    />
                  ) : (
                    <p className="text-sm text-gray-800 font-rubik mt-1">{config[key]}%</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-4 bg-blue-50 border border-blue-100 rounded-lg p-3" data-testid="config-note">
          <p className="text-xs text-blue-700 font-rubik">
            <strong>Note:</strong> These limits should match your Gemini API tier. The free tier has significantly lower limits.
            Upgrade to a paid tier for higher rate limits (typically 100x+). Visit{' '}
            <a href="https://ai.google.dev/gemini-api/docs/rate-limits" target="_blank" rel="noopener noreferrer" className="underline">
              Google AI Studio
            </a>{' '}
            to check your current tier and manage quotas.
          </p>
        </div>
      </div>
    </div>
  );
}

export default AdminApiUsage;
